import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { AssistantOrchestratorService } from '../services/assistant-orchestrator.service';
import { VoiceCommandRegistryService } from '../services/voice-command-registry.service';
import { SpeechToTextService } from 'src/shared/services/speech-to-text/speech-to-text.service';
import { VoiceSynthesisService } from 'src/shared/services/voice/voice-synthesis.service';
import {
  ASSISTANT_ARTIFACT_KEYS,
  ASSISTANT_ARTIFACT_IMAGE_BY_ID,
  type AssistantArtifactId,
} from './assistant-artifacts.config';
import { ASSISTANT_QUICK_ACTION_TILES } from './assistant-quick-actions.config';
import { VOICE_ASSISTANT_MARK_SRC } from './voice-assistant-mark.config';
import type { UserRoleHint } from './types/voice-assistant.types';
import { ASSISTANT_NAVIGATION_ENABLED } from './assistant-feature-flags';
import { AssistantConversationStateService } from '../chat/orchestrator/assistant-conversation-state.service';
import type { WorkflowChipOption } from '../chat/models/conversation-state.model';
import { WORKFLOW_SLOT_GENERATION_PATH } from '../chat/models/conversation-state.model';
import { EducationalContentViewComponent } from '../chat/knowledge-setup/educational-content-view.component';
import { formatEducationalContentPlainText } from '../chat/knowledge-setup/educational-content-view-model';
import type {
  VoiceChatAssistantEducationalMessage,
  VoiceChatMessage,
} from './models/voice-chat-message.model';
import { isVoiceChatAssistantMessage } from './models/voice-chat-message.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-voice-assistant-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, EducationalContentViewComponent],
  templateUrl: './voice-assistant-panel.component.html',
  styleUrl: './voice-assistant-panel.component.css',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class VoiceAssistantPanelComponent {
  readonly voiceAssistantMarkSrc = VOICE_ASSISTANT_MARK_SRC;

  readonly orchestrator = inject(AssistantOrchestratorService);
  readonly registry = inject(VoiceCommandRegistryService);
  readonly speech = inject(SpeechToTextService);
  readonly conversationState = inject(AssistantConversationStateService);
  private readonly tts = inject(VoiceSynthesisService);

  /** When false, hide chrome that duplicates the FAB sheet title */
  @Input() showTitle = true;

  /** Voice + educational form hybrid: show workflow chips, hide duplicate artifact tiles. */
  @Input() hybridEducationalLayout = false;

  @Input() unifiedPopupLayout = false;

  @Input() sheetFixedChrome = false;

  @Output() openResourceSetup = new EventEmitter<AssistantArtifactId>();

  @HostBinding('class.voice-assistant-panel--sheet')
  get sheetHostLayout(): boolean {
    return this.sheetFixedChrome && this.unifiedPopupLayout;
  }

  @ViewChild('manualInput') manualInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('scrollHost') scrollHostRef?: ElementRef<HTMLElement>;
  manualText = '';

  readonly quickChips = this.registry.getQuickCommandPhrases();
  readonly artifactKeys = [...ASSISTANT_ARTIFACT_KEYS];
  readonly selectedEducationalTool = signal<
    AssistantArtifactId | 'general_chat' | null
  >(null);
  readonly eduToolsExpanded = signal(false);
  readonly quickActionTiles = ASSISTANT_QUICK_ACTION_TILES;
  readonly waveformBarIndexes = Array.from({ length: 14 }, (_, i) => i);

  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;
  readonly copySucceeded = signal(false);

  readonly ttsState = toSignal(this.tts.getState$(), {
    initialValue: {
      speaking: false,
      paused: false,
      error: null as string | null,
    },
  });

  readonly ttsSpeaking = computed(() => this.ttsState().speaking);

  readonly isTeacherCapturing = computed(() => {
    const state = this.orchestrator.assistantState();
    const session = this.orchestrator.listenSessionActive();
    const assistantBusy =
      state === 'thinking' ||
      state === 'responding' ||
      this.ttsSpeaking();
    return (
      (session && !assistantBusy) ||
      state === 'listening' ||
      this.speechState().isRecording
    );
  });

  readonly showAssistantSpeaking = computed(
    () =>
      !this.isTeacherCapturing() &&
      (this.ttsSpeaking() ||
        this.orchestrator.assistantState() === 'responding')
  );

  readonly showWaveform = computed(
    () => this.isTeacherCapturing() || this.showAssistantSpeaking()
  );

  readonly showDiscoveryChrome = computed(
    () =>
      this.unifiedPopupLayout &&
      !this.orchestrator.voiceDiscoveryDismissed() &&
      !this.conversationState.generatedEducationalView()
  );

  /** Guided/custom or quick workflow: chips + prompts without full hybrid form. */
  readonly awaitingGenerationPathChoice = computed(
    () =>
      this.conversationState.workflowSessionActive() &&
      this.conversationState.generationPathMode() === 'unset' &&
      this.conversationState.currentSlotKey() === WORKFLOW_SLOT_GENERATION_PATH
  );

  readonly showWorkflowLayout = computed(
    () =>
      this.hybridEducationalLayout ||
      this.conversationState.isQuickWorkflowActive() ||
      this.awaitingGenerationPathChoice() ||
      (this.conversationState.workflowSessionActive() &&
        this.conversationState.generationPathMode() !== 'unset')
  );

  private readonly speechState = toSignal(this.speech.getSpeechState(), {
    initialValue: {
      isRecording: false,
      finalTranscript: '',
      interimTranscript: '',
      error: null as string | null,
      errorCode: null,
    },
  });

  private lastLoggedFinalLength = 0;
  private scrollFollowupTimers: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    effect(() => {
      const s = this.speechState();
      if (!s) return;
      const combined = this.speech.getCombinedTranscript(s);
      if (s.isRecording && combined) {
        this.manualText = combined;
      }
      if (s.finalTranscript.length > this.lastLoggedFinalLength) {
        this.lastLoggedFinalLength = s.finalTranscript.length;
        if (!environment.production) {
          console.info('[assistant] input updated from voice', {
            length: combined.length,
          });
        }
      }
    });

    effect(() => {
      const msgs = this.conversationState.chatMessages();
      const last = msgs.at(-1);
      this.orchestrator.liveTranscript();
      const eduView = this.conversationState.generatedEducationalView();
      const reply = this.orchestrator.aiReply();
      if (
        !msgs.length &&
        !eduView &&
        !reply &&
        !this.orchestrator.liveTranscript()?.trim()
      ) {
        return;
      }
      const emphasizeAssistant =
        !!last && isVoiceChatAssistantMessage(last);
      this.scheduleScrollToLatest(emphasizeAssistant);
    });

    effect(() => {
      const state = this.orchestrator.assistantState();
      if (state === 'responding') {
        this.scheduleScrollToLatest(true);
      }
    });
  }

  /** Keep the latest assistant bubble in view (after layout / educational embed). */
  private scheduleScrollToLatest(emphasizeAssistant = false): void {
    for (const id of this.scrollFollowupTimers) {
      clearTimeout(id);
    }
    this.scrollFollowupTimers = [];

    const run = () => this.scrollToLatestResponse(emphasizeAssistant);
    queueMicrotask(run);
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    for (const delay of emphasizeAssistant ? [80, 200, 450] : [50]) {
      this.scrollFollowupTimers.push(setTimeout(run, delay));
    }
  }

  private scrollToLatestResponse(emphasizeAssistant = false): void {
    const scrollHost = this.scrollHostRef?.nativeElement;
    if (!scrollHost) {
      this.scrollLatestAssistantInDocument();
      return;
    }

    if (emphasizeAssistant) {
      const assistantRows = scrollHost.querySelectorAll<HTMLElement>(
        '.va-chat-row--assistant'
      );
      const lastAssistant = assistantRows[assistantRows.length - 1];
      if (lastAssistant && this.scrollElementWithinHost(lastAssistant, scrollHost)) {
        return;
      }
    }

    const anchor = scrollHost.querySelector<HTMLElement>('.va-chat-scroll-anchor');
    if (anchor && this.scrollElementWithinHost(anchor, scrollHost)) {
      return;
    }

    scrollHost.scrollTop = scrollHost.scrollHeight;
  }

  private scrollElementWithinHost(el: HTMLElement, host: HTMLElement): boolean {
    const padding = 16;
    const elRect = el.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    if (elRect.height === 0 && elRect.width === 0) {
      return false;
    }
    let delta = 0;
    if (elRect.bottom > hostRect.bottom - padding) {
      delta = elRect.bottom - hostRect.bottom + padding;
    } else if (elRect.top < hostRect.top + padding) {
      delta = elRect.top - hostRect.top - padding;
    }
    if (delta !== 0) {
      host.scrollTop += delta;
      return true;
    }
    return true;
  }

  /** Legacy layout has no scroll host; scroll the last assistant row in the page. */
  private scrollLatestAssistantInDocument(): void {
    const rows = document.querySelectorAll<HTMLElement>('.va-chat-row--assistant');
    const last = rows[rows.length - 1];
    last?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }

  isLatestAssistantChatMessage(msg: VoiceChatMessage): boolean {
    if (!isVoiceChatAssistantMessage(msg)) return false;
    return msg.id === this.conversationState.latestAssistantMessageId();
  }

  assistantChatMessageHasActions(msg: VoiceChatMessage): boolean {
    if (!isVoiceChatAssistantMessage(msg)) return false;
    if (msg.kind === 'text') return !!msg.text.trim();
    if (msg.kind === 'quick') return true;
    if (msg.kind === 'educational') {
      return !!(msg.text?.trim() || msg.view);
    }
    return false;
  }

  chatEducationalArtifactKey(
    msg: VoiceChatAssistantEducationalMessage
  ): string | null {
    return (
      msg.artifactKey ??
      this.conversationState.sessionArtifact() ??
      (this.selectedEducationalTool() !== 'general_chat'
        ? this.selectedEducationalTool()
        : null)
    );
  }

  hasAssistantInput(): boolean {
    return !!(
      this.manualText.trim() ||
      this.speech.getCombinedTranscript().trim()
    );
  }

  artifactIconSrc(id: AssistantArtifactId): string {
    return ASSISTANT_ARTIFACT_IMAGE_BY_ID[id];
  }

  get sttSupported(): boolean {
    return this.speech.isSupported();
  }

  onMicClick(): void {
    if (!this.sttSupported) return;
    this.orchestrator.toggleListenSession();
  }

  onSendManual(): void {
    this.submitAssistantInput('send');
  }

  /** Copies STT text into the pill input (same source as live transcript). */
  commitVoiceToInput(): string {
    const combined = this.speech.getCombinedTranscript();
    if (combined) {
      this.manualText = combined;
    }
    return this.manualText.trim();
  }

  /**
   * Single entry for typed text and voice transcript (same as handleAssistantInput).
   */
  submitAssistantInput(source: 'mic' | 'send' | 'enter' = 'send'): void {
    this.speech.commitInterimToFinal();
    const text = this.commitVoiceToInput();
    if (!text) return;

    if (!environment.production) {
      console.info('[assistant] send clicked', { source });
    }
    this.orchestrator.processUserMessage(text, true);
    this.manualText = '';
    this.lastLoggedFinalLength = 0;
    this.speech.clearTranscript();
    this.orchestrator.liveTranscript.set('');
  }

  onChip(phrase: string): void {
    this.orchestrator.processUserMessage(phrase, true);
  }

  onWorkflowChip(chip: WorkflowChipOption): void {
    this.orchestrator.processWorkflowChip(
      chip.slotKey,
      chip.value,
      true,
      chip.label
    );
  }

  onQuickAction(commandId: string): void {
    this.registry
      .runCommandById(commandId, this.inferRole())
      .pipe(take(1))
      .subscribe(result => {
        if (!result) return;
        if (result.action && ASSISTANT_NAVIGATION_ENABLED) {
          this.orchestrator.executeAction(result.action);
        } else if (result.replySpoken) {
          this.orchestrator.aiReply.set(result.replySpoken);
          this.conversationState.appendAssistantText(result.replySpoken);
        }
      });
  }

  onArtifactNavigate(artifact: AssistantArtifactId): void {
    this.openResourceSetup.emit(artifact);
  }

  toggleEducationalTools(): void {
    this.eduToolsExpanded.update(open => !open);
  }

  selectEducationalTool(artifact: AssistantArtifactId): void {
    this.selectedEducationalTool.set(artifact);
    this.eduToolsExpanded.set(false);
    this.orchestrator.bootstrapEducationalArtifact(artifact);
  }

  selectGeneralChat(): void {
    this.selectedEducationalTool.set('general_chat');
    this.eduToolsExpanded.set(false);
    this.focusGeneralChat();
  }

  focusGeneralChat(): void {
    queueMicrotask(() => this.manualInputRef?.nativeElement?.focus());
  }

  retry(): void {
    this.orchestrator.lastError.set(null);
    this.orchestrator.assistantState.set('idle');
  }

  stopAll(): void {
    this.orchestrator.endListenSession();
    this.orchestrator.cancelSpeech();
  }

  /**
   * Hide response card when guided workflow prompt is authoritative
   * (duplicate text, or stale aiReply from prior slot before followUp$ completes).
   */
  shouldHideDuplicateWorkflowReply(): boolean {
    if (!this.showWorkflowLayout()) return false;
    const guided = this.conversationState.lastGuidedDisplayText()?.trim();
    const reply = this.orchestrator.aiReply()?.trim();
    if (!guided || !reply) return false;
    if (guided === reply) return true;
    return guided !== reply;
  }

  copyLastReply(): void {
    const text = this.conversationState.getLastAssistantSpeakableContent();
    if (text) {
      this.copyTextToClipboard(text);
      return;
    }
    const eduView = this.conversationState.generatedEducationalView();
    const fallback =
      (eduView && formatEducationalContentPlainText(eduView)) ||
      this.conversationState.lastGuidedDisplayText()?.trim();
    if (fallback) {
      this.copyTextToClipboard(fallback);
    }
  }

  private copyTextToClipboard(text: string): void {
    const markDone = () => {
      this.copySucceeded.set(true);
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => this.copySucceeded.set(false), 2000);
    };

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard
        .writeText(text)
        .then(markDone)
        .catch(() => this.fallbackCopy(text, markDone));
    } else {
      this.fallbackCopy(text, markDone);
    }
  }

  private fallbackCopy(text: string, done: () => void): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done();
    } catch {
      /* ignore */
    }
  }

  private inferRole(): UserRoleHint {
    try {
      const u = JSON.parse(
        (typeof window !== 'undefined' && localStorage.getItem('user')) || '{}'
      );
      const r = String(u.role || '').toLowerCase();
      if (r.includes('principal')) return 'principal';
      if (r.includes('admin')) return 'admin';
    } catch {
      /* ignore */
    }
    return 'teacher';
  }
}
